import { router, useNavigation } from 'expo-router';
import { useLayoutEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { StyleSheet, View } from 'react-native';

import { useEditIndividual } from '@/api/hooks/individualHooks';
import BrandedButton from '@/components/buttons/BrandedButton';
import GoBackButton from '@/components/buttons/GoBackButton';
import FormWrapper from '@/components/forms/FormWrapper';
import GuardWrapper from '@/components/GuardWrapper';
import TextInputField from '@/components/inputs/TextInputField';
import ProfilePicPickerField from '@/components/ProfilePicPickerField';
import Text from '@/components/Text';
import Title from '@/components/Title';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { IndividualData, individualResolver } from '@/lib/validations/IndividualSchema';
import { containsSection } from '@/utils/containsSection';
import { firebaseImage, ThumbSize } from '@/utils/firebaseImage';
import { i18n } from '@/utils/i18n';
import { uploadToFireStorage } from '@/utils/uploadToFireStorage';

const EditIndividual = () => {
  const { state } = useAuth();
  const navigation = useNavigation();
  const [isLoading, setIsLoading] = useState(false);
  const { showSuccess, showError } = useEndFlowModal();
  const { mutateAsync: editIndividual, isPending } = useEditIndividual();

  // Locales
  const edit_profile = i18n.t('profile.edit_profile');
  const update_label = i18n.t('mix.update');
  const success = i18n.t('end_flow.success_title');
  const success_message = i18n.t('profile.update_profile_success_message');
  const error = i18n.t('end_flow.error_title');
  const error_message = i18n.t('profile.update_profile_failure_message');
  const form_title = i18n.t('profile.your_profile');

  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => <Title label={edit_profile} />,
      headerLeft: () => <GoBackButton />
    });
  }, [edit_profile, navigation]);

  const {
    control,
    handleSubmit,
    formState: { isValid, submitCount }
  } = useForm<IndividualData>({
    resolver: individualResolver as any,
    defaultValues: {
      thumbnail: firebaseImage(state.user?.user?.thumbnail ?? '', ThumbSize.Small),
      biography: state.user?.user?.biography ?? ''
    },
    mode: 'onChange'
  });

  const updateProfile = async ({ biography, thumbnail }: { biography?: string; thumbnail: string | null }) => {
    await editIndividual(
      {
        userId: state.user?.user?.id ?? '',
        biography: biography,
        thumbnail: thumbnail
      },
      {
        onSuccess: () => {
          setIsLoading(false);
          showSuccess({
            title: success,
            message: success_message,
            onButtonPress: () => router.back()
          });
        },
        onError: () => {
          setIsLoading(false);
        }
      }
    );
  };

  const onSubmit: SubmitHandler<IndividualData> = async (data) => {
    try {
      setIsLoading(true);

      if (data.thumbnail && !containsSection(data.thumbnail, 'thumbs')) {
        await uploadToFireStorage(`individuals/${state.user?.user?.id}/thumbs/`, {
          name: `profilePic_${new Date().toISOString()}`,
          uri: data.thumbnail
        }).then((storagedFile) => {
          const file = storagedFile.metadata.fullPath;
          updateProfile({
            thumbnail: file,
            biography: data.biography ?? ''
          });
        });
      } else {
        updateProfile({
          thumbnail: null,
          biography: data.biography ?? ''
        });
      }
    } catch (e) {
      setIsLoading(false);
      console.error(e);
      showError({
        title: error,
        message: error_message
      });
    }
  };

  return (
    <GuardWrapper canAccess={['INDIVIDUAL']}>
      <FormWrapper>
        <View style={styles.mainContainer}>
          <ProfilePicPickerField name="thumbnail" control={control} />
          <View style={styles.fieldsContainer}>
            <Text style={styles.formTitle} fontWeight="500">
              {form_title}
            </Text>
            <TextInputField
              control={control}
              name="biography"
              multiline
              minHeight={120}
              maxHeight={120}
              label="Bio"
              placeholder={i18n.t('sign_in.nonProfit.general.description_placeholder')}
              returnKeyLabel="next"
              returnKeyType="next"
            />
          </View>
          <BrandedButton
            loading={isPending || isLoading}
            disabled={!isValid && submitCount > 0}
            onPress={handleSubmit(onSubmit)}>
            {update_label}
          </BrandedButton>
        </View>
      </FormWrapper>
    </GuardWrapper>
  );
};

const styles = StyleSheet.create({
  mainContainer: {
    flexDirection: 'column',
    rowGap: 25,
    paddingTop: 25
  },

  fieldsContainer: {
    flexDirection: 'column',
    rowGap: 15
  },

  formTitle: {
    fontSize: 22,
    color: 'black'
  }
});

export default EditIndividual;
